import { getTraceCapabilities } from './capabilities';
import { incUnmatchedOps } from './opsTelemetry';

// Shared operation router used by editor and tests
export function applyOperation(plotlyJson: any, op: string, payload: any): boolean {
  try {
    switch (op) {
      case 'op.perTraceFill:set': {
        const index = Number(payload?.index);
        const color = String(payload?.color || '');
        const tr = plotlyJson?.data?.[index];
        if (!tr) {
          console.warn('[op.perTraceFill:set] Trace not found', { index, dataLength: plotlyJson?.data?.length });
          return false;
        }
        const caps = getTraceCapabilities(tr as any);
        if (!caps.supportsPerTraceFill) {
          console.warn('[capabilities] Unsupported op', { type: tr?.type, op });
          return false;
        }
        const t = String((tr?.type || 'scatter')).toLowerCase();
        console.log('[op.perTraceFill:set] Applying fill color', { index, type: t, color, hasMarker: !!tr.marker, hasFill: !!tr.fill });
        if (t === 'box' || t === 'violin') {
          tr.fillcolor = color;
          if (tr.marker && tr.marker.color === undefined) {
            tr.marker.color = color;
          }
        } else if (t === 'indicator') {
          // Indicator traces: set gauge.bar.color (main bar color)
          if (!tr.gauge) tr.gauge = {};
          if (!tr.gauge.bar) tr.gauge.bar = {};
          tr.gauge.bar.color = color;
          // Lock the color so theme doesn't override manual changes
          if (!(tr as any)._lumiTheme) (tr as any)._lumiTheme = {};
          (tr as any)._lumiTheme.lockedBarColor = true;
          // Also set delta.value.color if delta exists
          if (tr.delta && tr.delta.value) {
            tr.delta.value.color = color;
            (tr as any)._lumiTheme.lockedDeltaColor = true;
          }
        } else {
          // For bar/histogram charts, marker.color controls the fill color
          // For scatter/line charts, marker.color controls the marker/point color
          const isBarChart = t === 'bar' || t === 'histogram';
          
          if (isBarChart) {
            // Bar charts: marker.color is the fill color
            if (!tr.marker) tr.marker = {};
            // Ensure per-trace categorical color takes precedence over any prior coloraxis/colorscale
            delete (tr as any).coloraxis;
            delete (tr as any).colorscale;
            delete (tr.marker as any).coloraxis;
            delete (tr.marker as any).colorscale;
            // Convert rgba() to hex if needed for bar charts (Plotly bar charts prefer hex)
            let finalColor = color;
            if (color.toLowerCase().startsWith('rgba(')) {
              const m = color.match(/rgba\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-1]?(?:\.\d+)?)\)/i);
              if (m) {
                const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
                const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
                const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
                const toHex = (n: number) => n.toString(16).padStart(2, '0');
                finalColor = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
              }
            }
            tr.marker.color = finalColor;
            console.log('[op.perTraceFill:set] Bar chart color set', { originalColor: color, finalColor, markerColor: tr.marker.color });
          } else {
            // Scatter/line charts
            if (!tr.marker) tr.marker = {};
            // Ensure per-trace categorical color takes precedence over any prior coloraxis/colorscale
            delete (tr as any).coloraxis;
            delete (tr as any).colorscale;
            delete (tr.marker as any).coloraxis;
            delete (tr.marker as any).colorscale;
            tr.marker.color = color;
            
            // For scatter traces with fill (like ridgeline plots), set fillcolor and line.color directly
            // Ridgeline plots use fillcolor without opacity suffix and line.color matching fillcolor
            if (tr.fill) {
              // Check if this is a ridgeline-style plot (has fill and fillcolor was set without opacity)
              // or if fillcolor already exists without opacity suffix (indicates ridgeline)
              const existingFillcolor = tr.fillcolor;
              const isRidgelineStyle = existingFillcolor && 
                typeof existingFillcolor === 'string' && 
                !existingFillcolor.match(/[0-9a-f]{2}$/i) && // No 2-digit hex suffix (opacity)
                existingFillcolor.length <= 7; // Standard hex color length (#RRGGBB)
              
              if (isRidgelineStyle || (plotlyJson.layout?.meta?.chart_type === 'ridgeline')) {
                // Ridgeline plots: set fillcolor directly and match line.color
                tr.fillcolor = color;
                if (!tr.line) tr.line = {};
                tr.line.color = color;
              } else {
                // Regular filled scatter: add opacity suffix
                tr.fillcolor = color + '40';
              }
            }
            
            // If the system previously managed per-point colors for a single bar trace, keep array coloring responsive
            if ((tr as any)._lumiTheme?.managedPerPoint && Array.isArray(tr?.x) || Array.isArray(tr?.y)) {
              const count = Array.isArray(tr.x) ? tr.x.length : (Array.isArray(tr.y) ? tr.y.length : 0);
              if (count > 1) {
                const palette = (plotlyJson.layout?.colorway as string[]) || [color];
                const row = palette && palette.length ? palette : [color];
                tr.marker.color = Array.from({ length: count }, (_, i) => row[i % row.length]);
              }
            }
          }
        }
        return true;
      }
      case 'op.continuousScale:set': {
        const scale = String(payload?.scale || 'Viridis');
        // Mirror editor apply sequence
        setNestedProperty(plotlyJson, 'layout.meta.lumi.theme.sequential.scale', scale);
        // The editor also normalizes colors and applies theme.continuous; replicate core effect
        try {
          // Update coloraxis with the new scale
          (plotlyJson.layout as any).coloraxis = { ...(plotlyJson.layout as any).coloraxis, colorscale: scale };
          // Ensure z-based traces are bound to coloraxis (handles any trace type with z data)
          const hasZ = (tr: any) => tr && tr.z && (Array.isArray(tr.z) || Array.isArray(tr.z?.[0]));
          if (Array.isArray(plotlyJson.data)) {
            (plotlyJson.data as any[]).forEach((tr: any) => {
              if (hasZ(tr)) {
                tr.coloraxis = tr.coloraxis || 'coloraxis';
                if (tr.colorscale) delete tr.colorscale;
                if (tr.autocolorscale) delete tr.autocolorscale;
              }
            });
            plotlyJson.data = [...plotlyJson.data];
          }
        } catch {}
        return true;
      }
      case 'op.sliceColor:set': {
        const index = Number(payload?.index);
        const sliceIndex = Number(payload?.sliceIndex);
        const color = String(payload?.color || '');
        const tr = plotlyJson?.data?.[index];
        if (!tr) return false;
        const caps = getTraceCapabilities(tr as any);
        if (!caps.supportsSliceColors) {
          console.warn('[capabilities] Unsupported op', { type: tr?.type, op });
          return false;
        }
        if (!tr.marker) tr.marker = {};
        const labels:any[] = (tr.labels || tr.label || []) as any[];
        const len = Array.isArray(labels) ? labels.length : (Array.isArray(tr.values) ? tr.values.length : 0);
        const ensure = (arr:any, def:string) => {
          const base = Array.isArray(arr) ? arr.slice() : new Array(len).fill(def);
          while (base.length < len) base.push(def);
          return base;
        };
        const colors = ensure(tr.marker.colors, color || '#1f77b4');
        if (sliceIndex >= 0 && sliceIndex < len) colors[sliceIndex] = color;
        tr.marker.colors = colors;
        return true;
      }
      case 'op.sankeyNodeColor:set': {
        const index = Number(payload?.index);
        const nodeIndex = Number(payload?.nodeIndex);
        const color = String(payload?.color || '');
        const tr = plotlyJson?.data?.[index];
        if (!tr) return false;
        const caps = getTraceCapabilities(tr as any);
        if (!caps.supportsNodeColors) {
          console.warn('[capabilities] Unsupported op', { type: tr?.type, op });
          return false;
        }
        if (!tr.node) tr.node = {};
        const labels:any[] = (tr.node.labels || tr.node.label || []) as any[];
        const len = Array.isArray(labels) ? labels.length : 0;
        const ensure = (arr:any, def:string) => {
          const base = Array.isArray(arr) ? arr.slice() : new Array(len).fill(def);
          while (base.length < len) base.push(def);
          return base;
        };
        const colors = ensure(tr.node.color, color || '#1f77b4');
        if (nodeIndex >= 0 && nodeIndex < len) colors[nodeIndex] = color;
        tr.node.color = colors;
        // Lock node colors from theme updates when manually changed
        if (!(tr as any)._lumiTheme) (tr as any)._lumiTheme = {};
        (tr as any)._lumiTheme.lockedNodeColors = true;
        return true;
      }
      case 'op.sankeyResetTheme:set': {
        const index = Number(payload?.index);
        const tr = plotlyJson?.data?.[index];
        if (!tr || tr.type !== 'sankey') return false;
        // Unlock colors so theme can reapply
        if ((tr as any)._lumiTheme) {
          delete (tr as any)._lumiTheme.lockedNodeColors;
          delete (tr as any)._lumiTheme.lockedLinkColors;
        }
        // Bump editrevision to trigger theme reapplication
        if (plotlyJson.layout) {
          (plotlyJson.layout as any).editrevision = ((plotlyJson.layout as any).editrevision || 0) + 1;
        }
        return true;
      }
      case 'op.indicatorStepColor:set': {
        const index = Number(payload?.index);
        const stepIndex = Number(payload?.stepIndex); // 0=poor, 1=satisfactory, 2=good
        const color = String(payload?.color || '');
        const tr = plotlyJson?.data?.[index];
        if (!tr || tr.type !== 'indicator') return false;
        if (!tr.gauge) tr.gauge = {};
        if (!Array.isArray(tr.gauge.steps)) tr.gauge.steps = [];
        if (stepIndex >= 0 && stepIndex < tr.gauge.steps.length) {
          tr.gauge.steps[stepIndex].color = color;
          // Lock step colors when manually changed
          if (!(tr as any)._lumiTheme) (tr as any)._lumiTheme = {};
          if (!(tr as any)._lumiTheme.lockedStepColors) (tr as any)._lumiTheme.lockedStepColors = {};
          (tr as any)._lumiTheme.lockedStepColors[stepIndex] = true;
        }
        return true;
      }
      case 'op.indicatorThresholdColor:set': {
        const index = Number(payload?.index);
        const color = String(payload?.color || '');
        const tr = plotlyJson?.data?.[index];
        if (!tr || tr.type !== 'indicator') return false;
        if (!tr.gauge) tr.gauge = {};
        if (!tr.gauge.threshold) tr.gauge.threshold = {};
        if (!tr.gauge.threshold.line) tr.gauge.threshold.line = {};
        tr.gauge.threshold.line.color = color;
        // Lock threshold color when manually changed
        if (!(tr as any)._lumiTheme) (tr as any)._lumiTheme = {};
        (tr as any)._lumiTheme.lockedThresholdColor = true;
        return true;
      }
      default:
        incUnmatchedOps(op);
        console.warn('[capabilities] unmatched op', op);
        return false;
    }
  } catch (e) {
    console.warn('[capabilities] op error', op, e);
    return false;
  }
}

function setNestedProperty(obj: any, path: string, value: any) {
  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!current[parts[i]]) current[parts[i]] = {};
    current = current[parts[i]];
  }
  current[parts[parts.length - 1]] = value;
}


